<template>
  <BlockFieldset class="project-invoice-approval">
    <div class="verification-flex m-1">
      <VerificationState
        :project="project"
        verificationStateType="invoiceApprovementVerificationState"
        :readonly="isStateReadOnly"
        :changeState="changeState"
        :canDecide="canDecide"
      />
      <Card>
        <template #title>{{ $t('invoiceApprovementNote') }} </template>
        <template #content>
          <b-form-textarea
            v-model="project.invoiceApprovementNote"
            placeholder="..."
            rows="4"
            max-rows="4"
          ></b-form-textarea>
        </template>
      </Card>
    </div>

    <div v-if="hasSaveButton" class="text-right mt-2">
      <b-button variant="bg" :disabled="isSaving" @click="save">
        {{ $t('saveNamed', { name: $t('project_invoice_approval') }) }}
      </b-button>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Card from 'primevue/card';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import VerificationState from '@/components/VerificationState.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { toProjectInvoiceApprovalInput } from './projectInvoiceApprovalInput';

/** Number of the unsaved project (ProjectEdit `_new`): its note goes with `createProject`. */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Block project-invoice-approval ("Rechnungen: Freigabe", see feature page
 * projekt-angebote-rechnungen): approval state of the employer, changed immediately, and its
 * note with own save button. In Matrix-Authz the level decides every state change; in
 * Legacy-Authz `VerificationState` keeps its role rules. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectInvoiceApproval',
  components: { BlockFieldset, Card, VerificationState },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  computed: {
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled']),
    blockInput() {
      return toProjectInvoiceApprovalInput(this.project);
    },
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    hasSaveButton() {
      return !this.isBlockReadOnly && !this.isNewProject;
    },
    /** The state mutation needs a stored project number. */
    isStateReadOnly() {
      return this.isBlockReadOnly || this.isNewProject;
    },
    /** Matrix-Authz: the block level decides; Legacy-Authz: null keeps the role rules. */
    canDecide() {
      return this.isOrganizationRoleNavigationEnabled ? !this.isBlockReadOnly : null;
    },
  },
  methods: {
    ...mapActions(['updateProjectInvoiceApproval', 'updateProjectInvoiceApprovalState']),
    saveBlockInput(input) {
      return this.updateProjectInvoiceApproval({ projectNumber: this.project.number, input });
    },
    changeState(verificationState) {
      return this.updateProjectInvoiceApprovalState({
        projectNumber: this.project.number,
        verificationState,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.verification-flex {
  display: flex;
  gap: 1em;
  flex-flow: row wrap;
  flex-basis: 100%;
  justify-content: flex-start;
}

.verification-flex > div {
  flex: 1;
  min-width: 350px;
}
</style>
